import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import "@/App.css";
import axios from 'axios';
import { LanguageProvider, useLanguage } from '@/context/LanguageContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { AnnouncerProvider } from '@/components/accessibility/AnnouncerContext';
import { Toaster } from '@/components/ui/toaster';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import VATCalculator from '@/components/VATCalculator';
import SkipLinks from '@/components/accessibility/SkipLinks';
import AccessibilityWidget from '@/components/accessibility/AccessibilityWidget';
import CookieBanner from '@/components/CookieBanner';
import SEOHead from '@/components/SEOHead';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Loader2 } from 'lucide-react';
import {
  EzoicProvider,
  EzoicAfterCalculator,
  EzoicAfterFAQ,
  EzoicBetweenSections,
  EzoicSidebar
} from '@/components/EzoicAds';

// Lazy loaded components for better performance (code splitting)
const BulkCalculator = lazy(() => import('@/components/BulkCalculator'));
const InvoiceCalculator = lazy(() => import('@/components/InvoiceCalculator'));
const CalculationHistory = lazy(() => import('@/components/CalculationHistory'));
const VATRatesTable = lazy(() => import('@/components/VATRatesTable'));
const PrivacyPolicy = lazy(() => import('@/components/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('@/components/CookiePolicy'));
const TermsOfService = lazy(() => import('@/components/TermsOfService'));
const FAQSection = lazy(() => import('@/components/FAQSection'));
const CountryGuide = lazy(() => import('@/components/CountryGuide'));
const SEOContent = lazy(() => import('@/components/SEOContent'));
const SEOCountryContent = lazy(() => import('@/components/SEOCountryContent'));
const HeroSection = lazy(() => import('@/components/HeroSection'));
const LocalVATCalculator = lazy(() => import('@/components/LocalVATCalculator'));
const BlogPage = lazy(() => import('@/components/blog/BlogPage'));
const BlogArticlePage = lazy(() => import('@/components/blog/BlogArticlePage'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Supported languages for routing
const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'it', 'es'];

// Country slug mapping for SEO URLs
const COUNTRY_SLUGS = {
  // EU Countries
  'AT': 'austria', 'BE': 'belgium', 'BG': 'bulgaria', 'HR': 'croatia',
  'CY': 'cyprus', 'CZ': 'czech-republic', 'DK': 'denmark', 'EE': 'estonia',
  'FI': 'finland', 'FR': 'france', 'DE': 'germany', 'GR': 'greece',
  'HU': 'hungary', 'IE': 'ireland', 'IT': 'italy', 'LV': 'latvia',
  'LT': 'lithuania', 'LU': 'luxembourg', 'MT': 'malta', 'NL': 'netherlands',
  'PL': 'poland', 'PT': 'portugal', 'RO': 'romania', 'SK': 'slovakia',
  'SI': 'slovenia', 'ES': 'spain', 'SE': 'sweden',
  // Non-EU Europe
  'GB': 'united-kingdom', 'CH': 'switzerland', 'NO': 'norway', 'IS': 'iceland',
  'RS': 'serbia', 'BA': 'bosnia', 'AL': 'albania', 'MK': 'north-macedonia',
  'ME': 'montenegro', 'UA': 'ukraine', 'TR': 'turkey',
  // Rest of World
  'AU': 'australia', 'NZ': 'new-zealand', 'CA': 'canada', 'IN': 'india',
  'JP': 'japan', 'KR': 'south-korea', 'SG': 'singapore', 'MY': 'malaysia',
  'TH': 'thailand', 'PH': 'philippines', 'ID': 'indonesia', 'VN': 'vietnam',
  'ZA': 'south-africa', 'BR': 'brazil', 'MX': 'mexico', 'AR': 'argentina',
  'CL': 'chile', 'CO': 'colombia', 'AE': 'uae', 'SA': 'saudi-arabia',
  'IL': 'israel', 'EG': 'egypt', 'NG': 'nigeria', 'KE': 'kenya'
};

// Reverse mapping: slug -> country code
const SLUG_TO_CODE = Object.fromEntries(
  Object.entries(COUNTRY_SLUGS).map(([code, slug]) => [slug, code])
);

// FAQSection.js is generic marketing FAQ with zero country reference - verbatim
// identical across the homepage and all 310 vat-calculator-* pages in a given
// language (confirmed engine #3 of the 3 duplicate-content generators flagged
// in eb8ba5e, never addressed since). SEOCountryContent already renders its own
// FAQ per country (mentions name/rate/tax name), so this block adds nothing on
// country pages beyond duplicate boilerplate. Piloted here on the 3 country
// samples flagged dormant on 2026-10-01 (en:JP, de:NO, de:ID) instead of a
// sitewide removal - measure at 10 days, then propose full rollout.
const SUPPRESS_GENERIC_FAQ = new Set(['en:JP', 'de:NO', 'de:ID']);

// Main App Content with routing logic
function AppContent() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('calculator');
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [preselectedCountryCode, setPreselectedCountryCode] = useState(null);

  // Detect if on Privacy page
  const isPrivacyPage = useMemo(() => {
    return location.pathname.includes('/privacy');
  }, [location.pathname]);

  // Detect if on Cookie Policy page
  const isCookiePage = useMemo(() => {
    return location.pathname.includes('/cookies');
  }, [location.pathname]);

  // Detect if on Terms of Service page
  const isTermsPage = useMemo(() => {
    return location.pathname.includes('/terms');
  }, [location.pathname]);

  // Detect if on any legal page
  const isLegalPage = isPrivacyPage || isCookiePage || isTermsPage;

  // Parse locale and country from URL path
  const { locale, countrySlug } = useMemo(() => {
    const path = location.pathname;
    let detectedLocale = null;
    let detectedCountrySlug = null;
    
    // Check if path starts with a language code
    const pathParts = path.split('/').filter(Boolean);
    
    if (pathParts.length > 0) {
      // Check first segment for locale
      if (SUPPORTED_LANGUAGES.includes(pathParts[0])) {
        detectedLocale = pathParts[0];
      }
      
      // Look for vat-calculator-{country} pattern in any segment
      for (const part of pathParts) {
        if (part.startsWith('vat-calculator-')) {
          detectedCountrySlug = part.replace('vat-calculator-', '');
          break;
        }
      }
    }
    
    return { locale: detectedLocale, countrySlug: detectedCountrySlug };
  }, [location.pathname]);

  // Sync language from URL locale parameter
  useEffect(() => {
    // If there's a locale in URL and it's different from current language, sync it
    if (locale && SUPPORTED_LANGUAGES.includes(locale) && locale !== language) {
      setLanguage(locale);
    }
    // If no locale in URL, the page is always English - sync regardless of pathname
    else if (!locale && language !== 'en') {
      setLanguage('en');
    }
  }, [locale, language, setLanguage, location.pathname]);

  // Sync country from URL country slug parameter
  useEffect(() => {
    if (countrySlug && SLUG_TO_CODE[countrySlug]) {
      setPreselectedCountryCode(SLUG_TO_CODE[countrySlug]);
    }
  }, [countrySlug]);

  // Preselect country when countries are loaded
  useEffect(() => {
    if (preselectedCountryCode && countries.length > 0) {
      const country = countries.find(c => c.code === preselectedCountryCode);
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [preselectedCountryCode, countries]);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/countries`);
        setCountries(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch countries:', err);
        setError('Failed to load countries data');
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  // Handle reuse from history
  const handleReuseCalculation = useCallback((data) => {
    setActiveTab('calculator');
  }, []);

  // Handle country selection - update URL for SEO
  const handleCountrySelect = useCallback((country) => {
    setSelectedCountry(country);
    
    // Update URL for SEO when country is selected
    if (country && COUNTRY_SLUGS[country.code]) {
      const langPrefix = language !== 'en' ? `/${language}` : '';
      const newPath = `${langPrefix}/vat-calculator-${COUNTRY_SLUGS[country.code]}`;
      
      // Only navigate if path is different
      if (location.pathname !== newPath) {
        navigate(newPath, { replace: true });
      }
    }
  }, [language, location.pathname, navigate]);

  // Get current page for SEO
  const getCurrentPage = useMemo(() => {
    // Legal pages render their own component regardless of activeTab/countrySlug
    // (see isLegalPage above) - SEOHead must know about them too, otherwise it
    // falls back to the calculator/home SEO data and emits that page's canonical
    // URL instead of the legal page's own (found 2026-09-18 on /fr/terms).
    if (isPrivacyPage) return 'privacy';
    if (isCookiePage) return 'cookies';
    if (isTermsPage) return 'terms';
    if (countrySlug) return 'country';
    switch (activeTab) {
      case 'calculator': return 'calculator';
      case 'bulk': return 'bulk';
      case 'invoice': return 'invoice';
      case 'rates': return 'rates';
      case 'history': return 'home';
      default: return 'home';
    }
  }, [activeTab, countrySlug, isPrivacyPage, isCookiePage, isTermsPage]);

  // Render active tab content
  const renderContent = () => {
    // Render Privacy Policy page
    if (isPrivacyPage) {
      return <PrivacyPolicy />;
    }

    // Render Cookie Policy page
    if (isCookiePage) {
      return <CookiePolicy />;
    }

    // Render Terms of Service page
    if (isTermsPage) {
      return <TermsOfService />;
    }

    if (loading) {
      return (
        <div 
          className="flex items-center justify-center min-h-[400px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <Loader2 className="w-8 h-8 animate-spin text-primary" aria-hidden="true" />
          <span className="sr-only">Loading countries data...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div 
          className="flex items-center justify-center min-h-[400px]"
          role="alert"
          aria-live="assertive"
        >
          <div className="text-center">
            <p className="text-destructive mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="text-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-4 py-2"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'calculator':
        return (
          <>
            {/* Hero Section with SEO content - only show on main calculator page without country selected */}
            {!selectedCountry && (
              <Suspense fallback={<LoadingFallback />}>
                <HeroSection />
              </Suspense>
            )}
            
            {/* Calculator */}
            <div id="calculator-section" className={!selectedCountry ? 'mt-8' : ''}>
              <VATCalculator 
                countries={countries} 
                onCountrySelect={handleCountrySelect}
                preselectedCountry={selectedCountry}
              />
            </div>
          </>
        );
      case 'bulk':
        return <BulkCalculator />;
      case 'invoice':
        return <InvoiceCalculator countries={countries} />;
      case 'rates':
        return <VATRatesTable countries={countries} />;
      case 'history':
        return <CalculationHistory onReuse={handleReuseCalculation} />;
      default:
        return (
          <>
            {!selectedCountry && (
              <Suspense fallback={<LoadingFallback />}>
                <HeroSection />
              </Suspense>
            )}
            <div id="calculator-section" className={!selectedCountry ? 'mt-8' : ''}>
              <VATCalculator 
                countries={countries} 
                onCountrySelect={handleCountrySelect}
                preselectedCountry={selectedCountry}
              />
            </div>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* SEO Head */}
      <SEOHead page={getCurrentPage} country={selectedCountry} locale={locale} />
      
      {/* Skip Links for keyboard navigation - WCAG 2.4.1 */}
      <SkipLinks />
      
      {/* Screen reader announcements container */}
      <div 
        id="sr-announcements" 
        role="status" 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
      />
      
      <Header 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
      />
      
      <main 
        id="main-content"
        className="flex-1 container mx-auto px-4 py-8" 
        data-testid="main-content"
        tabIndex={-1}
        role="main"
        aria-label="Main content"
      >
        {/* Breadcrumbs for SEO */}
        {!loading && !error && (
          <Breadcrumbs 
            country={selectedCountry} 
            pageType={isPrivacyPage ? 'privacy' : isCookiePage ? 'cookies' : isTermsPage ? 'terms' : 'calculator'}
          />
        )}
        
        {/* Heading for screen readers */}
        <h1 className="sr-only">
          VAT Calculator - {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          {selectedCountry ? ` - ${selectedCountry.name}` : ''}
        </h1>
        
        {renderContent()}
        
        {/* Ezoic Ad - After Calculator */}
        {!isLegalPage && !loading && !error && activeTab === 'calculator' && (
          <div className="my-6">
            <EzoicAfterCalculator />
          </div>
        )}

        {/* Country Guide - SEO Content for country-specific pages */}
        {!isLegalPage && !loading && !error && selectedCountry && activeTab === 'calculator' && (
          <>
            <CountryGuide country={selectedCountry} countries={countries} />

            {/* Ezoic Ad - Between Sections */}
            <div className="my-6">
              <EzoicBetweenSections />
            </div>

            <Suspense fallback={<LoadingFallback />}>
              <SEOCountryContent country={selectedCountry} showFull={true} />
            </Suspense>
          </>
        )}

        {/* FAQ Section - Only show on calculator pages for SEO */}
        {!isLegalPage && !loading && !error && activeTab === 'calculator' && !(
          selectedCountry && SUPPRESS_GENERIC_FAQ.has(`${language}:${selectedCountry.code}`)
        ) && (
          <FAQSection maxItems={6} />
        )}

        {/* Ezoic Ad - After FAQ */}
        {!isLegalPage && !loading && !error && activeTab === 'calculator' && (
          <div className="my-6">
            <EzoicAfterFAQ />
          </div>
        )}
      </main>
      
      <Footer />
      
      {/* Accessibility Widget - Floating button */}
      <AccessibilityWidget />
      
      {/* Cookie Consent Banner - GDPR Compliant */}
      <CookieBanner />
      
      <Toaster />
    </div>
  );
}

// Wrapper to handle routes with locale
function LocaleWrapper() {
  return <AppContent />;
}

// Main App with Router
function App() {
  return (
  <BrowserRouter>
    <HelmetProvider>
      <ThemeProvider>
        <LanguageProvider>
          <AnnouncerProvider>
            <EzoicProvider>
            <ScrollToTop />
            <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Root path */}
              <Route path="/" element={<LocaleWrapper />} />
              
              {/* Language-specific home pages */}
              <Route path="/de" element={<LocaleWrapper />} />
              <Route path="/fr" element={<LocaleWrapper />} />
              <Route path="/it" element={<LocaleWrapper />} />
              <Route path="/es" element={<LocaleWrapper />} />
              
              {/* Privacy Policy pages - all languages */}
              <Route path="/privacy" element={<LocaleWrapper />} />
              <Route path="/de/privacy" element={<LocaleWrapper />} />
              <Route path="/fr/privacy" element={<LocaleWrapper />} />
              <Route path="/it/privacy" element={<LocaleWrapper />} />
              <Route path="/es/privacy" element={<LocaleWrapper />} />
              
              {/* Cookie Policy pages - all languages */}
              <Route path="/cookies" element={<LocaleWrapper />} />
              <Route path="/de/cookies" element={<LocaleWrapper />} />
              <Route path="/fr/cookies" element={<LocaleWrapper />} />
              <Route path="/it/cookies" element={<LocaleWrapper />} />
              <Route path="/es/cookies" element={<LocaleWrapper />} />
              
              {/* Terms of Service pages - all languages */}
              <Route path="/terms" element={<LocaleWrapper />} />
              <Route path="/de/terms" element={<LocaleWrapper />} />
              <Route path="/fr/terms" element={<LocaleWrapper />} />
              <Route path="/it/terms" element={<LocaleWrapper />} />
              <Route path="/es/terms" element={<LocaleWrapper />} />
              
              {/* Local VAT term calculator pages - DDV, PVN, KDV, TVA, MwSt */}
              <Route path="/ddv-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/pvn-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/kdv-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/tva-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/mwst-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/mwst-rechner" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              
              {/* Local VAT with language prefix */}
              <Route path="/de/ddv-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/de/pvn-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/de/kdv-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/de/tva-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/de/mwst-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/de/mwst-rechner" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              
              <Route path="/fr/ddv-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/fr/pvn-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/fr/kdv-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/fr/tva-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/fr/mwst-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              
              <Route path="/it/ddv-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/it/pvn-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/it/kdv-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/it/tva-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/it/mwst-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              
              <Route path="/es/ddv-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/es/pvn-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/es/kdv-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/es/tva-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              <Route path="/es/mwst-calculator" element={<Suspense fallback={<LoadingFallback />}><LocalVATCalculator /></Suspense>} />
              
              {/* Blog pages */}
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/de/blog" element={<BlogPage />} />
              <Route path="/fr/blog" element={<BlogPage />} />
              <Route path="/it/blog" element={<BlogPage />} />
              <Route path="/es/blog" element={<BlogPage />} />
              
              {/* Blog article pages */}
              <Route path="/blog/:slug" element={<BlogArticlePage />} />
              <Route path="/de/blog/:slug" element={<BlogArticlePage />} />
              <Route path="/fr/blog/:slug" element={<BlogArticlePage />} />
              <Route path="/it/blog/:slug" element={<BlogArticlePage />} />
              <Route path="/es/blog/:slug" element={<BlogArticlePage />} />
              
              {/* English country-specific pages (no locale prefix) */}
              <Route path="/vat-calculator-:countrySlug" element={<LocaleWrapper />} />
              
              {/* Language + country-specific pages */}
              <Route path="/:locale/vat-calculator-:countrySlug" element={<LocaleWrapper />} />
              
              {/* Catch-all for locale paths */}
              <Route path="/:locale" element={<LocaleWrapper />} />
              
              {/* Fallback */}
              <Route path="*" element={<LocaleWrapper />} />
            </Routes>
            </Suspense>
            </EzoicProvider>
          </AnnouncerProvider>
        </LanguageProvider>
      </ThemeProvider>
    </HelmetProvider>
  </BrowserRouter>
  );
}

export default App;
